import React, { useMemo, useCallback } from "react";
import { Container } from "@mui/material";
import { timeToLocale } from "../../../../../../utils/timeConversion";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { RAW_DATA_LAYOUT } from "../../../../../../utils/headers";
import CustomPagination from "./common/Pagination";
import { CustomToolbar } from "./common/CustomToolbar";
import _ from "lodash";

const GENUS_FIELDS = RAW_DATA_LAYOUT.map((column) => column.field).filter(
  (field) => field.toLowerCase().includes("genus"),
);

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& ::-webkit-scrollbar": {
    borderTop: "1px solid #e9e9ec",
    height: "7px",
    width: "7px",
  },
  "& ::-webkit-scrollbar-thumb:horizontal": {
    borderRadius: "15px",
    backgroundColor: "#6d6d6dc9",
  },
  "& ::-webkit-scrollbar-thumb:vertical": {
    borderRadius: "15px",
    backgroundColor: "#6d6d6dc9",
  },
  "& .MuiDataGrid-scrollbar--horizontal": {
    display: "block",
  },
  overflowX: "auto",
  overflowY: "auto",
  width: "100%",
  height: "100%",
}));

export default function RawDataPreview({
  data,
  timeZone,
  loading,
  columnVisibilityModel,
  setColumnVisibilityModel,
  dataGridRef,
  onRowClick,
  columns,
}) {
  const { parsedData, hasData } = useMemo(() => {
    let parsedData = Array.isArray(data) ? data : [];

    if (parsedData.length === 1 && parsedData[0] === "no match data") {
      parsedData = [];
    }

    return { parsedData, hasData: parsedData.length > 0 };
  }, [data]);

  const rowsWithId = useMemo(() => {
    if (!hasData) return [];

    return parsedData.map((row, index) => {
      const updatedRow = {
        ...row,
        id: index + 1,
        imaging_date_time: row.imaging_date_time
          ? timeToLocale(row.imaging_date_time, { timeZone })
          : "",
      };

      for (const key of GENUS_FIELDS) {
        if (typeof updatedRow[key] === "string") {
          updatedRow[key] = _.capitalize(updatedRow[key]);
        }
      }
      return updatedRow;
    });
  }, [parsedData, hasData, timeZone]);

  // Memoize column visibility handler
  const handleColumnVisibilityChange = useCallback(
    (newModel) => {
      if (Object.values(newModel).length === 0) {
        setColumnVisibilityModel(
          _.mapValues(columnVisibilityModel, () => true),
        );
      } else {
        setColumnVisibilityModel(newModel);
      }
    },
    [setColumnVisibilityModel, columnVisibilityModel],
  );

  return (
    <div
      style={{
        paddingTop: "1rem",
        paddingBottom: "1rem",
        fontSize: "11px",
        width: "100%",
        height: "100%",
      }}
    >
      {/* the full width of the report, like every other report: a capped
          container left wide gutters on large screens */}
      <Container
        maxWidth={false}
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <StyledDataGrid
          rows={rowsWithId}
          columns={columns}
          loading={loading}
          localeText={{ noRowsLabel: "No Data" }}
          slots={{
            toolbar: CustomToolbar,
            pagination: CustomPagination,
          }}
          showToolbar
          // no grid filter, from the toolbar or a column's menu: the report's
          // filters decide the rows, and the download is what they returned
          disableColumnFilter
          apiRef={dataGridRef}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
              },
            },
          }}
          disableDensitySelector={true}
          pageSizeOptions={[10, 20, 50]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          getRowId={(row) => row.id}
          disableVirtualization={false}
          // a row opens its specimen
          onRowClick={onRowClick ? (params) => onRowClick(params.row) : undefined}
          sx={onRowClick ? { "& .MuiDataGrid-row": { cursor: "pointer" } } : undefined}
        />
      </Container>
    </div>
  );
}
